// The words of the daily routine (/day, components/routine/, and the routine mode of the study and of Your
// positions). English and Spanish side by side: a key missing in one fails `npm run verify`.
import { words } from "../services/i18n.ts";

const s = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const t = words({
    en: {
        title: "Today's training",
        // The card on the home (decided 2026-11-01): the boards of the day, the weakness it trains, one button
        weakness: (area: string, times: number) => `What costs you most: ${area}.`
            + (times === 0 ? "" : times === 1 ? " Today you train it once." : times === 2 ? " Today you train it twice."
                : ` Today you train it ${times} times.`),
        stripOpening: "Opening",
        stripFind: "Find",
        stripConvert: "Advantage",
        startMinutes: "Start · 15 min",
        onTheWayTitle: "Your training is on its way",
        onTheWayLead: "Every day, five exercises taken from your own games. For the first one, only this is left:",
        stepWhere: "Where you play",
        stepWhereTodo: "Connect Lichess or chess.com",
        stepWhereDone: "Connected",
        stepGames: "Your games",
        stepGamesComing: "Coming in now",
        stepGamesDone: (n: number) => `${n} game${s(n, "", "s")}`,
        stepAnalysis: "The analysis of your games",
        stepAnalysisRunning: "Under way: the engine does it, there is nothing for you to do",
        stepAnalysisDone: "Done",
        stepAnalysisNext: "It starts once your games are in",
        stepFirst: "Your first training",
        stepFirstNote: "As soon as the analysis is over",
        meanwhile: "Meanwhile",
        meanwhileText: "Practise one of your openings against what players really answer.",
        meanwhileGo: "Practise now",
        factTitle: "From your games",
        titleOf: (day: string) => `Training of ${day}`,
        progress: (done: number, total: number) => `${done} of ${total} done`,
        reading: "Getting your day ready…",
        failed: "Your training could not be read right now.",
        signedOut: "Log in to see your training.",
        noGames: "Your training is made from your own games, and there are none yet.",
        connect: "Connect where you play",
        nothing: "There is nothing for this day: your games did not leave anything new to train.",
        nothingToday: "Nothing new to train today. Analyse more games and there will be.",
        analyse: "Analyse your games",

        // The parts
        openingTitle: "An opening of yours",
        findOpeningTitle: "Find the move · opening",
        findMiddlegameTitle: "Find the move · middlegame",
        convertTitle: "Keep the advantage",
        openingWorst: (games: number, points: string) =>
            `Your last ${games} ${s(games, "game", "games")} here: ${points}. You can do better.`,
        openingBest: (games: number, points: string) =>
            `Your last ${games} ${s(games, "game", "games")} here: ${points}. One of your best: keep it sharp.`,
        openingPlays: (played: number, needed: number) => `Played ${played} of ${needed} times`,
        findOpeningLead: "The moment one of your openings went wrong. What should you have played?",
        findMiddlegameLead: "A serious error of yours in a middlegame. Find a better move.",
        convertLead: "You were winning here and let it go. Keep it for five moves.",
        fromGame: (opponent: string, when: string) => `Your game against ${opponent}, ${when}`,
        fromGameNoDate: (opponent: string) => `Your game against ${opponent}`,

        // States
        done: "Done",
        solved: "Found",
        partial: "Nearly",
        missed: "Missed",
        skipped: "Seen",
        inProgress: "In progress",
        needsLichess: "Play it against the moves real players made: connect your Lichess account. You do not need to play on Lichess.",
        connectLichess: "Connect Lichess",
        howLichess: "How it works",
        seePosition: "See the position",
        seenContinue: "Seen: continue",
        lichessTitle: "Play against real opponents",
        lichessWhy: "In this exercise your opponent plays the moves real players of your level made in this position. Those moves come from the Lichess opening explorer, which only answers to a Lichess account.",
        lichessNoNeed: "You do not need to play on Lichess. You only need an account there, which is free, and to let Sparring Chess read the explorer with it.",
        lichessStep1: "No Lichess account? Create one: it takes a minute.",
        lichessCreate: "Create a free account",
        lichessStep2: "Connect it here and give permission. You come back to your training.",
        lichessConnectNow: "Connect Lichess",
        lichessNotNow: "Not now: you can see the position and its moves, and carry on with your training.",
        lichessSee: "See the position and continue",
        close: "Close",
        gone: "This game is no longer in your account.",

        // Actions
        start: "Start",
        continueIt: "Continue",
        startDay: "Start today's training",
        startOther: "Start this training",
        continueDay: "Continue your training",
        play: "Play",
        playAgain: "Play it again",
        tryAgain: "Try it again",
        next: "Next exercise",
        backToDay: "Back to your training",
        opening: "Opening the board…",

        // Missing parts
        missing: "Not in today's training",
        missing_no_openings: "an opening: none of yours has been played often enough lately",
        missing_openings_used: "an opening: this week has used every one there was",
        missing_no_positions: "positions of this kind: analyse more games to have them",
        missing_positions_used: "positions of this kind: this week has used every one there was",

        // The end of the day
        dayDone: "Training done",
        dayDoneLead: "That is today's training. Tomorrow there is another one.",
        dayDoneOther: "That day's training is done.",
        summaryFound: (found: number, total: number) =>
            `You found ${found} of ${total} ${s(total, "position", "positions")} at the first try.`,
        summaryOpening: (plays: number) => `You played the opening ${plays} ${s(plays, "time", "times")}.`,
        askRating: "How useful was today's training?",
        ratingLow: "Not at all",
        ratingHigh: "Very",
        commentLabel: "Anything you would change? (optional)",
        commentPlaceholder: "Too easy, too long, an exercise that made no sense…",
        send: "Send",
        sending: "Sending…",
        thanks: "Thank you: it is how tomorrow's gets better.",
        change: "Change it",
        sendFailed: "It could not be sent. Try again in a moment.",

        // The routine bar on the study and on Your positions
        barLabel: (slot: number, total: number) => `Today's training · exercise ${slot} of ${total}`,
        barOpening: (played: number, needed: number) =>
            played >= needed ? `Played ${played} ${s(played, "time", "times")}: you can go on, or play it again.`
                : `Play it ${needed} times before going on: ${played} of ${needed} so far.`,
        barMoment: "Answer it, and your training carries on from here.",
    },
    es: {
        title: "Entrenamiento de hoy",
        weakness: (area: string, times: number) => `Lo que más te cuesta: ${area}.`
            + (times === 0 ? "" : times === 1 ? " Hoy lo entrenas una vez." : times === 2 ? " Hoy lo entrenas dos veces."
                : ` Hoy lo entrenas ${times} veces.`),
        stripOpening: "Apertura",
        stripFind: "Encuentra",
        stripConvert: "Ventaja",
        startMinutes: "Empezar · 15 min",
        onTheWayTitle: "Tu entrenamiento está en camino",
        onTheWayLead: "Cada día, cinco ejercicios sacados de tus propias partidas. Para el primero solo falta esto:",
        stepWhere: "Dónde juegas",
        stepWhereTodo: "Conecta Lichess o chess.com",
        stepWhereDone: "Conectado",
        stepGames: "Tus partidas",
        stepGamesComing: "Llegando ahora",
        stepGamesDone: (n: number) => `${n} partida${s(n, "", "s")}`,
        stepAnalysis: "El análisis de tus partidas",
        stepAnalysisRunning: "En marcha: lo hace el motor, no tienes que hacer nada",
        stepAnalysisDone: "Hecho",
        stepAnalysisNext: "Empieza cuando lleguen tus partidas",
        stepFirst: "Tu primer entrenamiento",
        stepFirstNote: "En cuanto termine el análisis",
        meanwhile: "Mientras tanto",
        meanwhileText: "Practica una de tus aperturas contra lo que te responden de verdad.",
        meanwhileGo: "Practicar ahora",
        factTitle: "De tus partidas",
        titleOf: (day: string) => `Entrenamiento del ${day}`,
        progress: (done: number, total: number) => `${done} de ${total} hechos`,
        reading: "Preparando tu día…",
        failed: "No se ha podido leer tu entrenamiento ahora mismo.",
        signedOut: "Inicia sesión para ver tu entrenamiento.",
        noGames: "Tu entrenamiento se hace con tus propias partidas, y aún no hay ninguna.",
        connect: "Conecta dónde juegas",
        nothing: "No hay nada para este día: tus partidas no han dejado nada nuevo que entrenar.",
        nothingToday: "Hoy no hay nada nuevo que entrenar. Analiza más partidas y lo habrá.",
        analyse: "Analiza tus partidas",

        openingTitle: "Una apertura tuya",
        findOpeningTitle: "Encuentra la jugada · apertura",
        findMiddlegameTitle: "Encuentra la jugada · medio juego",
        convertTitle: "Mantén la ventaja",
        openingWorst: (games: number, points: string) =>
            `Tus últimas ${games} ${s(games, "partida", "partidas")} aquí: ${points}. Puedes mejorarla.`,
        openingBest: (games: number, points: string) =>
            `Tus últimas ${games} ${s(games, "partida", "partidas")} aquí: ${points}. De las mejores: mantenla a punto.`,
        openingPlays: (played: number, needed: number) => `Jugada ${played} de ${needed} veces`,
        findOpeningLead: "El momento en que una de tus aperturas se torció. ¿Qué tendrías que haber jugado?",
        findMiddlegameLead: "Un error serio tuyo en un medio juego. Encuentra una jugada mejor.",
        convertLead: "Aquí ibas ganando y lo dejaste escapar. Mantén la ventaja cinco jugadas.",
        fromGame: (opponent: string, when: string) => `Tu partida contra ${opponent}, ${when}`,
        fromGameNoDate: (opponent: string) => `Tu partida contra ${opponent}`,

        done: "Hecho",
        solved: "Encontrada",
        partial: "Casi",
        missed: "Fallada",
        skipped: "Vista",
        inProgress: "En curso",
        needsLichess: "Juégala contra las jugadas de jugadores reales: conecta tu cuenta de Lichess. No hace falta que juegues en Lichess.",
        connectLichess: "Conecta Lichess",
        howLichess: "Cómo funciona",
        seePosition: "Ver la posición",
        seenContinue: "Vista: continuar",
        lichessTitle: "Juega contra rivales reales",
        lichessWhy: "En este ejercicio tu rival juega las jugadas que hicieron jugadores reales de tu nivel en esta posición. Esas jugadas salen del explorador de aperturas de Lichess, que solo responde a una cuenta de Lichess.",
        lichessNoNeed: "No hace falta que juegues en Lichess. Solo necesitas tener cuenta allí, que es gratis, y dar permiso a Sparring Chess para consultar el explorador con ella.",
        lichessStep1: "¿No tienes cuenta de Lichess? Créala: es un minuto.",
        lichessCreate: "Crea una cuenta gratis",
        lichessStep2: "Conéctala aquí y da permiso. Vuelves a tu entrenamiento.",
        lichessConnectNow: "Conecta Lichess",
        lichessNotNow: "Ahora no: puedes ver la posición y sus jugadas, y seguir con tu entrenamiento.",
        lichessSee: "Ver la posición y seguir",
        close: "Cerrar",
        gone: "Esta partida ya no está en tu cuenta.",

        start: "Empezar",
        continueIt: "Continuar",
        startDay: "Empieza el entrenamiento de hoy",
        startOther: "Empieza este entrenamiento",
        continueDay: "Sigue con tu entrenamiento",
        play: "Jugar",
        playAgain: "Jugarla otra vez",
        tryAgain: "Intentarlo otra vez",
        next: "Siguiente ejercicio",
        backToDay: "Vuelve a tu entrenamiento",
        opening: "Abriendo el tablero…",

        missing: "No está en el entrenamiento de hoy",
        missing_no_openings: "una apertura: ninguna tuya se ha jugado lo bastante últimamente",
        missing_openings_used: "una apertura: esta semana ya ha usado todas las que había",
        missing_no_positions: "posiciones de este tipo: analiza más partidas para tenerlas",
        missing_positions_used: "posiciones de este tipo: esta semana ya ha usado todas las que había",

        dayDone: "Entrenamiento hecho",
        dayDoneLead: "Ese es el entrenamiento de hoy. Mañana hay otro.",
        dayDoneOther: "El entrenamiento de ese día está hecho.",
        summaryFound: (found: number, total: number) =>
            `Has encontrado ${found} de ${total} ${s(total, "posición", "posiciones")} a la primera.`,
        summaryOpening: (plays: number) => `Has jugado la apertura ${plays} ${s(plays, "vez", "veces")}.`,
        askRating: "¿Te ha servido el entrenamiento de hoy?",
        ratingLow: "Nada",
        ratingHigh: "Mucho",
        commentLabel: "¿Cambiarías algo? (opcional)",
        commentPlaceholder: "Demasiado fácil, demasiado largo, un ejercicio que no tenía sentido…",
        send: "Enviar",
        sending: "Enviando…",
        thanks: "Gracias: así es como el de mañana sale mejor.",
        change: "Cambiarla",
        sendFailed: "No se ha podido enviar. Inténtalo de nuevo en un momento.",

        barLabel: (slot: number, total: number) => `Entrenamiento de hoy · ejercicio ${slot} de ${total}`,
        barOpening: (played: number, needed: number) =>
            played >= needed ? `Jugada ${played} ${s(played, "vez", "veces")}: puedes seguir, o jugarla otra vez.`
                : `Juégala ${needed} veces antes de seguir: ${played} de ${needed} de momento.`,
        barMoment: "Respóndela, y tu entrenamiento sigue desde aquí.",
    },
});
